import type {
  ExpertTrialBalanceColumnValues,
  ExpertTrialBalanceComponent,
  ExpertTrialBalanceReport,
} from '../types/expertTrialBalanceReport'
import type {
  ExpertTrialBalanceCell,
  ExpertTrialBalanceControl,
  ExpertTrialBalancePeriodColumn,
  ExpertTrialBalanceRow,
  ExpertTrialBalanceViewData,
} from '../types/expertTrialBalanceView'

const EMPTY_CELL: ExpertTrialBalanceCell = {
  closingBalance: 0,
  credit: 0,
  debit: 0,
}

const EMPTY_VIEW_DATA: ExpertTrialBalanceViewData = {
  control: null,
  periods: [],
  rows: [],
  totals: null,
}

const mapColumnValuesToCells = (
  columnValues: ExpertTrialBalanceColumnValues,
  periods: ExpertTrialBalancePeriodColumn[],
): ExpertTrialBalanceCell[] =>
  periods.map((period) => {
    const cell = columnValues[period.key]

    if (!cell) {
      return EMPTY_CELL
    }

    return {
      closingBalance: cell.closingBalance,
      credit: cell.credit,
      debit: cell.debit,
    }
  })

const mapAccountComponent = (
  component: Extract<ExpertTrialBalanceComponent, { type: 'account' }>,
  periods: ExpertTrialBalancePeriodColumn[],
): ExpertTrialBalanceRow => ({
  accountNumber: component.accountNumber,
  id: component.id,
  label: component.label,
  values: mapColumnValuesToCells(component.column_values, periods),
})

// The API returns rows pre-ordered — do not sort in this file.
export const selectTrialBalanceView = (report: ExpertTrialBalanceReport | undefined): ExpertTrialBalanceViewData => {
  if (!report) {
    return EMPTY_VIEW_DATA
  }

  const periods: ExpertTrialBalancePeriodColumn[] = report.header.map((periodHeader) => ({
    key: periodHeader.key,
    label: periodHeader.label,
  }))

  const rows: ExpertTrialBalanceRow[] = []
  let totals: ExpertTrialBalanceCell[] | null = null
  let control: ExpertTrialBalanceControl | null = null

  for (const component of report.components) {
    if (component.type === 'group') {
      for (const account of Object.values(component.children)) {
        rows.push(mapAccountComponent(account, periods))
      }

      continue
    }

    if (component.type === 'account') {
      rows.push(mapAccountComponent(component, periods))

      continue
    }

    if (component.type === 'sum') {
      const cells = mapColumnValuesToCells(component.column_values, periods)

      if (totals === null) {
        totals = cells

        continue
      }

      const currentPeriodClosingBalance = cells[0]?.closingBalance ?? 0

      control = {
        imbalance: currentPeriodClosingBalance,
        isBalanced: currentPeriodClosingBalance === 0,
      }
    }
  }

  return {
    control,
    periods,
    rows,
    totals,
  }
}
